import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { getReconciliations, createReconciliation } from '../../../api/erp';
import { toast } from 'react-toastify';
import BlurredValue from '../../../components/common/BlurredValue';

const WholesaleBankReconciliationPage = () => {
  const [recons, setRecons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ account_id: 1, statement_date: '', statement_balance: '', ledger_balance: '' });

  useEffect(() => { fetch(); }, []);
  const fetch = async () => { 
    setLoading(true);
    try { 
      // Assuming bank reconciliations will also be module-filtered in the future
      const data = await getReconciliations({ module: 'wholesale' });
      setRecons(data); 
    } catch (err) {
      console.error('Failed to load reconciliations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try { 
      await createReconciliation({ ...formData, module: 'wholesale' }); 
      toast.success('Reconciled successfully'); 
      setShowModal(false); 
      fetch(); 
    } catch { 
      toast.error('Failed to save reconciliation'); 
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Wholesale Bank Reconciliation</h1>
        <button 
          onClick={() => setShowModal(true)} 
          className="text-white px-4 py-2 rounded flex items-center gap-2 transition-colors shadow-sm"
          style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
        >
          <Plus size={16}/> New Recon
        </button>
      </div>

      <div className="bg-white rounded shadow-sm overflow-hidden border border-gray-100">
        <table className="w-full">
          <thead className="bg-gray-50 text-gray-700 uppercase text-xs">
            <tr>
              <th className="px-6 py-3 text-left">Date</th>
              <th className="px-6 py-3 text-left">Account</th>
              <th className="px-6 py-3 text-right">Statement Bal.</th>
              <th className="px-6 py-3 text-right">Ledger Bal.</th>
              <th className="px-6 py-3 text-right">Difference</th>
            </tr>
          </thead>
          <tbody className="text-sm divide-y divide-gray-100">
            {recons.length > 0 ? (
              recons.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-gray-600">{new Date(r.statement_date).toLocaleDateString()}</td>
                  <td className="px-6 py-4 font-medium text-gray-900">{r.account_name || 'Wholesale Bank Account'}</td>
                  <td className="px-6 py-4 text-right font-mono text-gray-900"><BlurredValue value={r.statement_balance} /></td>
                  <td className="px-6 py-4 text-right font-mono text-gray-900"><BlurredValue value={r.ledger_balance} /></td>
                  <td className={`px-6 py-4 text-right font-bold font-mono ${parseFloat(r.difference) !== 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    <BlurredValue value={r.difference} />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                  {loading ? 'Loading records...' : 'No wholesale bank reconciliations found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-100">
            <h2 className="text-xl font-bold mb-4 text-gray-900">New Wholesale Reconciliation</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statement Date *</label>
                <input 
                  type="date" 
                  className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-emerald-500 outline-none" 
                  onChange={e => setFormData({...formData, statement_date: e.target.value})} 
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statement Balance (₦) *</label>
                <input 
                  type="number" 
                  placeholder="0.00" 
                  className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-emerald-500 outline-none" 
                  onChange={e => setFormData({...formData, statement_balance: e.target.value})} 
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ledger Balance (₦) *</label>
                <input 
                  type="number" 
                  placeholder="0.00" 
                  className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-emerald-500 outline-none" 
                  onChange={e => setFormData({...formData, ledger_balance: e.target.value})} 
                  required 
                />
              </div>
              <div className="flex gap-2 justify-end pt-4 border-t border-gray-100">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-2 text-white rounded shadow-sm transition-all"
                  style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WholesaleBankReconciliationPage;
